import { useState, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import { ExportMenu } from '@/components/common/ExportMenu';
import { KpiStrip } from '@/components/common/KpiStrip';
import { PageLoader } from '@/components/common/LoadingSpinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChevronsUpDown, ChevronUp, ChevronDown, Search, Network } from '@/utils/icons';
import { TablePagination } from '@/components/common/TablePagination';
import { useNetworkSummary } from '@/hooks/useMetrics';
import { useNetworks } from '@/hooks/useNetworks';
import { NETWORK_TYPES, SEGMENTS } from '@/utils/constants';
import {
  createBrandedPdf,
  addMetricRow,
  addSectionHeading,
  addBrandedTable,
  downloadPdf,
  loadLogoForPdf,
  exportFilename,
} from '@/utils/pdf';
import { downloadCsv } from '@/utils/csv';
import { renderPieLabel, renderActiveShape } from '@/utils/calculations';
import { PIE_COLORS, BAR_COLORS } from '@/utils/chart-colors';
import { FilterChip } from '@/components/common/FilterChip';

export function Networks() {
  const { summary, loading: summaryLoading } = useNetworkSummary();

  // Chart hover state
  const [activeSegIndex, setActiveSegIndex] = useState<number | undefined>(undefined);

  // Table filters
  const [typeFilter, setTypeFilter] = useState('');
  const [segmentFilter, setSegmentFilter] = useState('');
  const [tunnelStateFilter, setTunnelStateFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    networks,
    total,
    totalPages,
    loading: networksLoading,
  } = useNetworks({
    type: typeFilter || undefined,
    segment: segmentFilter || undefined,
    tunnelState: tunnelStateFilter || undefined,
    search: debouncedSearch || undefined,
    page,
    limit,
    sortBy,
    sortOrder,
  });

  // Stabilize table wrapper height to prevent page jump during search/filter
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const stableHeightRef = useRef<number>(0);

  useLayoutEffect(() => {
    if (!tableWrapperRef.current) return;
    if (!networksLoading && networks.length >= limit) {
      stableHeightRef.current = tableWrapperRef.current.offsetHeight;
      tableWrapperRef.current.style.minHeight = '';
    } else if (stableHeightRef.current > 0) {
      tableWrapperRef.current.style.minHeight = `${stableHeightRef.current}px`;
    }
  }, [networks, networksLoading, limit]);

  const handleFilterChange = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const hasFilters = !!(debouncedSearch || typeFilter || segmentFilter || tunnelStateFilter);

  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setTypeFilter('');
    setSegmentFilter('');
    setTunnelStateFilter('');
    setPage(1);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortBy !== field)
      return <ChevronsUpDown className="dls-h-3.5 dls-w-3.5 dls-text-muted-foreground/50" />;
    return sortOrder === 'asc' ? (
      <ChevronUp className="dls-h-3.5 dls-w-3.5" />
    ) : (
      <ChevronDown className="dls-h-3.5 dls-w-3.5" />
    );
  };

  const SortableHeader = ({
    field,
    children,
    className = '',
  }: {
    field: string;
    children: React.ReactNode;
    className?: string;
  }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => handleSort(field)}
        className={`dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors dls-px-1 dls-py-0.5 dls-rounded ${className?.includes('text-center') ? 'mx-auto' : '-ml-1'}`}
      >
        {children}
        <SortIcon field={field} />
      </button>
    </TableHead>
  );

  // Segment chart data
  const segmentData = useMemo(
    () =>
      (summary?.segmentBreakdown ?? [])
        .filter((s) => s.count > 0)
        .sort((a, b) => b.count - a.count),
    [summary]
  );

  // CC tunnel load chart data
  const ccLoadData = useMemo(
    () => (summary?.ccTunnelLoad ?? []).sort((a, b) => b.tunnelCount - a.tunnelCount),
    [summary]
  );

  const handleExportPdf = async () => {
    if (!summary) return;

    const logoData = await loadLogoForPdf();

    const layout = createBrandedPdf({
      title: 'Network Inventory Report',
      subtitle: `${summary.total} networks`,
      logoData,
    });

    let y = addMetricRow(
      layout.doc,
      [
        { label: 'Total Networks', value: String(summary.total) },
        { label: 'Static (LAN)', value: String(summary.staticCount) },
        { label: 'Dynamic', value: String(summary.dynamicCount) },
        { label: 'WAN', value: String(summary.wanCount) },
        { label: 'Tunnels Connected', value: String(summary.tunnelConnectedCount) },
        { label: 'Tunnels Disconnected', value: String(summary.tunnelDisconnectedCount) },
      ],
      layout.startY,
      layout
    );

    // Segment breakdown table
    if (summary.segmentBreakdown.length > 0) {
      y = addSectionHeading(layout.doc, 'Segmentation Breakdown', y + 6, layout);
      y = addBrandedTable(
        layout.doc,
        ['Segment', 'Count'],
        summary.segmentBreakdown
          .filter((s) => s.count > 0)
          .sort((a, b) => b.count - a.count)
          .map((s) => [s.segment, s.count]),
        y,
        layout
      );
    }

    // CC tunnel load table
    if (summary.ccTunnelLoad.length > 0) {
      y = addSectionHeading(layout.doc, 'Cloud Connector Tunnel Load', y + 6, layout);
      y = addBrandedTable(
        layout.doc,
        ['Cloud Connector', 'Tunnel Count'],
        summary.ccTunnelLoad
          .sort((a, b) => b.tunnelCount - a.tunnelCount)
          .map((cc) => [cc.ccName, cc.tunnelCount]),
        y,
        layout
      );
    }

    // Network list table
    if (networks.length > 0) {
      y = addSectionHeading(layout.doc, 'Network List', y + 6, layout);
      addBrandedTable(
        layout.doc,
        ['Name', 'Node', 'Type', 'CIDR', 'VLAN', 'Segment', 'Tunnels', 'CC Destination'],
        networks.map((n) => [
          n.name,
          n.nodeName || '',
          n.type || '',
          n.cidr || '',
          n.vlanId ?? '',
          n.segmentCategory || '',
          `${n.connectedTunnelCount}/${n.tunnelCount}`,
          n.ccDestination || '',
        ]),
        y,
        layout
      );
    }

    downloadPdf(layout.doc, exportFilename('networks', 'pdf'));
  };

  const handleExportCsv = () => {
    if (!networks.length) return;

    downloadCsv(
      networks.map((n) => ({
        Name: n.name,
        Node: n.nodeName || '',
        Type: n.type || '',
        CIDR: n.cidr || '',
        VLAN: n.vlanId ?? '',
        Segment: n.segmentCategory || '',
        'Total Tunnels': n.tunnelCount,
        'Connected Tunnels': n.connectedTunnelCount,
        'Disconnected Tunnels': n.disconnectedTunnelCount,
        'Unknown Tunnels': n.unknownTunnelCount,
        'CC Destination': n.ccDestination || '',
        'Firewall Enabled': n.firewallEnabled ? 'Yes' : 'No',
      })),
      exportFilename('networks', 'csv'),
      { report: 'Networks' }
    );
  };

  if (summaryLoading) {
    return <PageLoader />;
  }

  return (
    <div className="dls-space-y-6">
      {/* Header */}
      <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <h1 className="dls-page-title dls-flex dls-items-center dls-gap-2"><Network className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Networks</h1>
          <p className="dls-text-muted-foreground">
            Network segmentation, tunnels, and connectivity overview
          </p>
        </div>
        <ExportMenu
          onExportPdf={handleExportPdf}
          onExportCsv={handleExportCsv}
          label="Export Networks"
        />
      </div>

      {/* Summary stat strip */}
      {summary && (
        <KpiStrip
          items={[
            { label: 'Total Networks', value: summary.total, tooltip: 'Total number of networks configured across all nodes.' },
            { label: 'Static (LAN)', value: summary.staticCount, tooltip: 'Static LAN networks with fixed IP addressing.' },
            { label: 'Dynamic (LAN)', value: summary.dynamicCount, tooltip: 'Networks with dynamic IP addressing (DHCP).' },
            { label: 'WAN', value: summary.wanCount, tooltip: 'Wide Area Network connections for upstream connectivity.' },
            { label: 'Connected', value: summary.tunnelConnectedCount, variant: 'success', tooltip: 'Tunnels in CONNECTED state to Cloud Connectors.' },
            { label: 'Disconnected', value: summary.tunnelDisconnectedCount + summary.tunnelUnknownCount, variant: summary.tunnelDisconnectedCount + summary.tunnelUnknownCount > 0 ? 'danger' : 'default', tooltip: 'Tunnels in DISCONNECTED or UNKNOWN state.' },
          ]}
        />
      )}

      {/* Charts: Segmentation + CC Tunnel Load */}
      <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
        {/* Segmentation donut chart */}
        <Card>
          <CardHeader>
            <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
              Segmentation
              <InfoTooltip text="Network distribution by segment category, derived from network naming patterns (BMS, Camera, Voice, WAN, etc.)." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {segmentData.length === 0 ? (
              <p className="dls-text-sm dls-text-muted-foreground dls-text-center dls-py-8">
                No segmentation data available.
              </p>
            ) : (
              <div className="dls-h-[280px] dls-flex dls-items-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={segmentData}
                      dataKey="count"
                      nameKey="segment"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      label={renderPieLabel}
                      labelLine={false}
                      activeIndex={activeSegIndex}
                      activeShape={renderActiveShape}
                      onMouseEnter={(_, index) => setActiveSegIndex(index)}
                      onMouseLeave={() => setActiveSegIndex(undefined)}
                    >
                      {segmentData.map((_, index) => (
                        <Cell key={`seg-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => [`${value} networks`, 'Count']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CC Tunnel Load bar chart */}
        <Card>
          <CardHeader>
            <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
              Cloud Connector Tunnel Load
              <InfoTooltip text="Number of tunnels terminating at each Cloud Connector. Uneven distribution may indicate capacity planning opportunities." />
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ccLoadData.length === 0 ? (
              <p className="dls-text-sm dls-text-muted-foreground dls-text-center dls-py-8">
                No tunnel data available.
              </p>
            ) : (
              <div className="dls-h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={ccLoadData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis
                      dataKey="ccName"
                      type="category"
                      width={120}
                      fontSize={12}
                      tickFormatter={(v) => (v.length > 16 ? `${v.slice(0, 14)}...` : v)}
                    />
                    <Tooltip formatter={(value: number) => [`${value} tunnels`, 'Tunnels']} />
                    <Bar dataKey="tunnelCount" radius={[0, 4, 4, 0]} barSize={20}>
                      {ccLoadData.map((_, index) => (
                        <Cell key={`cc-${index}`} fill={BAR_COLORS[index % BAR_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Network Table */}
      <div ref={tableWrapperRef}>
      <Card>
        <CardHeader>
          <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
            <CardTitle>Network Inventory</CardTitle>
            <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap">
              {debouncedSearch && (
                <FilterChip
                  label={`Search: "${debouncedSearch}"`}
                  onRemove={() => { setSearch(''); setDebouncedSearch(''); setPage(1); }}
                />
              )}
              {typeFilter && (
                <FilterChip label={`Type: ${typeFilter}`} onRemove={() => { setTypeFilter(''); setPage(1); }} />
              )}
              {segmentFilter && (
                <FilterChip label={`Segment: ${segmentFilter}`} onRemove={() => { setSegmentFilter(''); setPage(1); }} />
              )}
              {tunnelStateFilter && (
                <FilterChip label={`Tunnels: ${tunnelStateFilter}`} onRemove={() => { setTunnelStateFilter(''); setPage(1); }} />
              )}
              {hasFilters && (
                <Button variant="ghost" size="sm" className="dls-h-6 dls-text-xs" onClick={clearFilters}>
                  Clear all
                </Button>
              )}
              <div className="dls-relative">
                <Search className="dls-absolute dls-left-3 dls-top-1/2 -dls-translate-y-1/2 dls-h-4 dls-w-4 dls-text-muted-foreground" />
                <Input
                  placeholder="Search any field..."
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  className="dls-pl-9 dls-w-full sm:dls-w-64 dls-rounded-xl"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className={`dls-p-0 ${!networksLoading && networks.length > 0 ? 'dls-overflow-x-auto' : ''}`}>
            <Table className={!networksLoading && networks.length > 0 ? 'dls-min-w-[1100px]' : ''}>
              <TableHeader>
                <TableRow>
                  <SortableHeader field="name">Name</SortableHeader>
                  <SortableHeader field="nodeName">Node</SortableHeader>

                  {/* Type — sortable + filterable */}
                  <TableHead>
                    <div className="dls-flex dls-items-center dls-gap-1">
                      <button
                        type="button"
                        onClick={() => handleSort('type')}
                        className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                      >
                        Type
                        <SortIcon field="type" />
                      </button>
                      <Select
                        value={typeFilter || 'all'}
                        onValueChange={(v) => handleFilterChange(setTypeFilter)(v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter type</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          {NETWORK_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>

                  <SortableHeader field="cidr">CIDR</SortableHeader>
                  <SortableHeader field="vlanId">VLAN</SortableHeader>

                  {/* Segment — sortable + filterable */}
                  <TableHead>
                    <div className="dls-flex dls-items-center dls-gap-1">
                      <button
                        type="button"
                        onClick={() => handleSort('segmentCategory')}
                        className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                      >
                        Segment
                        <SortIcon field="segmentCategory" />
                      </button>
                      <Select
                        value={segmentFilter || 'all'}
                        onValueChange={(v) => handleFilterChange(setSegmentFilter)(v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter segment</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Segments</SelectItem>
                          {SEGMENTS.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>

                  {/* Tunnels — sortable + filterable */}
                  <TableHead>
                    <div className="dls-flex dls-items-center dls-gap-1">
                      <button
                        type="button"
                        onClick={() => handleSort('connectedTunnelCount')}
                        className="dls-flex dls-items-center dls-gap-1 hover:dls-text-foreground dls-transition-colors -dls-ml-1 dls-px-1 dls-py-0.5 dls-rounded"
                      >
                        Tunnels
                        <SortIcon field="connectedTunnelCount" />
                      </button>
                      <Select
                        value={tunnelStateFilter || 'all'}
                        onValueChange={(v) => handleFilterChange(setTunnelStateFilter)(v === 'all' ? '' : v)}
                      >
                        <SelectTrigger className="dls-h-6 dls-w-6 dls-p-0 dls-border-0 dls-shadow-none [&>svg]:dls-h-3 [&>svg]:dls-w-3 [&>svg]:dls-opacity-50">
                          <span className="dls-sr-only">Filter tunnel state</span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All States</SelectItem>
                          <SelectItem value="connected">Connected</SelectItem>
                          <SelectItem value="disconnected">Disconnected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </TableHead>

                  <SortableHeader field="ccDestination">CC Destination</SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {networksLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="dls-text-center dls-py-12">
                      <div className="dls-inline-block dls-animate-spin dls-rounded-full dls-h-6 dls-w-6 dls-border-b-2 dls-border-primary" />
                    </TableCell>
                  </TableRow>
                ) : networks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="dls-text-center dls-py-12 dls-text-sm dls-text-muted-foreground">
                      {hasFilters
                        ? 'No networks found matching the current filters.'
                        : 'No networks have been discovered yet.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  networks.map((network) => (
                    <TableRow key={network.id} className="hover:dls-bg-muted/50 dls-transition-colors">
                      <TableCell className="dls-font-medium">{network.name}</TableCell>
                      <TableCell className="dls-text-muted-foreground">
                        {network.nodeName || '\u2014'}
                      </TableCell>
                      <TableCell>
                        {network.type ? (
                          <span
                            className={cn(
                              'dls-inline-flex dls-items-center dls-rounded-full dls-px-2 dls-py-0.5 dls-text-xs dls-font-medium',
                              network.type === 'STATIC'
                                ? 'dls-bg-status-info/15 dls-text-status-info'
                                : network.type === 'DYNAMIC'
                                  ? 'dls-bg-neeve-tertiary/20 dls-text-neeve-primary'
                                  : 'dls-bg-neutral-quarternary dls-text-neutral'
                            )}
                          >
                            {network.type}
                          </span>
                        ) : (
                          '\u2014'
                        )}
                      </TableCell>
                      <TableCell className="dls-text-muted-foreground dls-font-mono dls-text-xs">
                        {network.cidr || '\u2014'}
                      </TableCell>
                      <TableCell className="dls-text-muted-foreground">
                        {network.vlanId ?? '\u2014'}
                      </TableCell>
                      <TableCell>
                        {network.segmentCategory ? (
                          <span className="dls-inline-flex dls-items-center dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2 dls-py-0.5 dls-text-xs dls-font-medium">
                            {network.segmentCategory}
                          </span>
                        ) : (
                          '\u2014'
                        )}
                      </TableCell>
                      <TableCell>
                        {network.tunnelCount > 0 ? (
                          <span className="dls-inline-flex dls-items-center dls-gap-1.5">
                            <span className="dls-text-status-online dls-font-medium">
                              {network.connectedTunnelCount}
                            </span>
                            <span className="dls-text-muted-foreground">/</span>
                            <span className="dls-text-muted-foreground">{network.tunnelCount}</span>
                            {network.disconnectedTunnelCount > 0 && (
                              <span className="dls-text-status-offline dls-text-xs">
                                ({network.disconnectedTunnelCount} down)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="dls-text-muted-foreground">{'\u2014'}</span>
                        )}
                      </TableCell>
                      <TableCell className="dls-text-muted-foreground">
                        {network.ccDestination || '\u2014'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

          <TablePagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={limit}
            onPageChange={setPage}
            onPageSizeChange={setLimit}
          />
        </CardContent>
      </Card>
      </div>
    </div>
  );
}
